import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiContext } from '../../../../../context/UiContext';

import ResumeUploader from './ResumeUploader';
import CandidateForm from './CandidateForm';
import ResumeParsingTipsModal from '../../../../../modals/ResumeParsingTipsModal';
import classes from './styles.module.scss';
import uploaderClasses from './ResumeUploader/styles.module.scss';
import formClasses from './CandidateForm/styles.module.scss';
import Button from '../../../../Button';
import AdminService from '../../../../../services/AdminService';

export default function IntelCandidate({
  handleClose,
  refetchCandidates,
  candidate,
}) {
  const { showModal } = useUiContext();
  const { t } = useTranslation();
  const [showUploader, setShowUploader] = useState(!candidate);
  const [resumeData, setResumeData] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [showTipsModal, setShowTipsModal] = useState(false);

  const handleUploadComplete = async (data, file) => {
    // Check duplicity using parsed personal info
    try {
      const email = data?.personalInfo?.email || '';
      const firstName = data?.personalInfo?.firstName || '';
      const lastName = data?.personalInfo?.lastName || '';

      const dup = await AdminService.checkCareerInfoDuplicity({
        email,
        firstName,
        lastName,
      });

      if (dup?.userId) {
        setDuplicateInfo(dup);
      } else {
        setDuplicateInfo(null);
      }
    } catch (e) {
      setDuplicateInfo(null);
    }

    setResumeData({
      ...data,
      resumeFile: file
        ? {
            displayName: file.name,
            fileName: file.name,
            fileSize: file.size,
            file,
          }
        : data?.resumeFile,
    });
    setShowUploader(false);
  };

  const handleSkip = () => {
    setShowUploader(false);
  };

  const handleNeedHelp = () => {
    setShowTipsModal(true);
  };

  const handleStartAgain = () => {
    setResumeData(null);
    setDuplicateInfo(null);
    setShowUploader(true);
    setHasFormChanges(false);
    setProgressData(null);
  };

  const renderDuplicateUploader = () => {
    const userId = duplicateInfo?.userId;
    return (
      <div className={uploaderClasses.uploader}>
        <div className={uploaderClasses.uploadArea}>
          <div className={uploaderClasses.uploadContent}>
            <div className={uploaderClasses.uploadText}>
              <h2 className={uploaderClasses.dragText}>Duplicate candidate</h2>
              <div style={{ height: 16 }} />
              <Button
                variant="blackWhite"
                height={45}
                onClick={() => {
                  if (userId)
                    window.open(
                      `/tools/candidates?candidate=${userId}`,
                      '_blank'
                    );
                }}
              >
                View candidate
              </Button>
              <div style={{ height: 12 }} />
              <Button
                variant="blackWhite"
                height={45}
                onClick={handleStartAgain}
              >
                {t(
                  'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.startAgain'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleAdded = () => {
    showModal({
      title: 'Success!',
      text: 'Candidate added successfully.',
      confirmButtonLabel: 'Got it',
      dismissButtonLabel: 'Add more',
      onConfirm: () => {
        handleClose();
        refetchCandidates?.();
      },
      onCancel: () => {
        setShowUploader(true);
        setDuplicateInfo(null);
        setResumeData(null);
      },
    });
  };

  let content = null;
  if (showUploader) {
    content = (
      <ResumeUploader
        onUploadComplete={handleUploadComplete}
        onSkip={handleSkip}
        onNeedHelp={handleNeedHelp}
      />
    );
  } else if (duplicateInfo) {
    content = renderDuplicateUploader();
  } else {
    content = (
      <div className={classes.formContainer}>
        <CandidateForm
          resumeData={resumeData}
          onAdded={handleAdded}
          refetchCandidates={refetchCandidates}
          candidate={candidate}
          onFormValuesChange={setHasFormChanges}
          onProgressChange={setProgressData}
        />
      </div>
    );
  }

  return (
    <>
      <div className={classes.overlay} onClick={handleClose} />
      <div className={classes.NewCandidate}>
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerContent}>
              <div className={classes.titleSection}>
                <h1 className={classes.title}>
                  {candidate?.id
                    ? t(
                        'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.updateCandidate'
                      )
                    : t(
                        'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.addNewCandidate'
                      )}
                </h1>
                {duplicateInfo && (
                  <button type="button" className={classes.duplicateButton}>
                    {t(
                      'dashboardComponents.Candidates.CandidatesList.AllCandidates.AddCandidate.duplicate'
                    )}
                  </button>
                )}
              </div>
              <div className={classes.headerRight}>
                {!showUploader && !duplicateInfo && progressData && (
                  <div className={formClasses.progressBarWrap}>
                    <div className={formClasses.progressBarContainer}>
                      <div
                        className={`${formClasses.progressBar} ${progressData.progressColor}`}
                        style={{ width: `${progressData.percent}%` }}
                      />
                    </div>
                    <span className={formClasses.progressText}>
                      {progressData.percent}%
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  className={classes.closeButton}
                  onClick={() => {
                    if (showUploader) {
                      handleClose();
                      return;
                    }

                    if (!hasFormChanges) {
                      handleClose();
                      return;
                    }

                    showModal({
                      title: 'Exit Screen',
                      text: 'Changes will be lost. Are you sure you want to exit?',
                      dismissButtonLabel: 'Yes',
                      confirmButtonLabel: 'No',
                      onConfirm: () => {},
                      onCancel: handleClose,
                    });
                  }}
                />
              </div>
            </div>
          </div>

          {content}
        </div>
      </div>
      <ResumeParsingTipsModal
        show={showTipsModal}
        handleClose={() => setShowTipsModal(false)}
      />
    </>
  );
}
